import { useState } from 'react';
import { useRouter } from 'next/router';
import debounce from 'lodash/debounce';
import { toast } from 'react-toastify';
import Button from 'epn-ui/umd/dist/components/Button';
import BaseToast from '@base/BaseToast';

// Config
import routes from '@config/routes.json';

// Helpers
import useI18n from '@i18n';
import apiHooks, { ICreateUseRequestResponse } from '@apiHooks';
import {
    ICreateSupportMessage,
    ISupportQuestionSuggestsDataAttributesFormatted,
    ISupportThemesAttributes,
} from '@api/types';
import api from '@api';

// Interfaces
import {
    IPreparedMessage,
    ITicketSubmitParams,
} from '@components/pages/support/CreateMessageModal/templates/interface';

// Components
import AskQuestionModal from '../AskQuestionModal';
import ChooseThemeModal from '../ChooseThemeModal';
import CreateMessageModal from '../CreateMessageModal';

export interface ISupportHeadProps {
    // Задать вопрос
    askQuestionsIsOpen: boolean;
    onAskQuestionChange: (state: boolean) => void;

    // Выбрать тему
    chooseThemeIsOpen: boolean;
    onChooseThemeChange: (state: boolean) => void;

    // Создать обращение
    createMessageIsOpen: boolean;
    onCreateMessageChange: (state: boolean) => void;

    // Mutate
    updateDialogs?: () => void;
    updateMessageCount: () => void;

    // InitValue
    preparedMessage?: IPreparedMessage;
}

const { useQuestionSuggests } = apiHooks;

const SupportHead: React.FC<ISupportHeadProps> = ({
    onAskQuestionChange,
    askQuestionsIsOpen,
    onChooseThemeChange,
    chooseThemeIsOpen,
    createMessageIsOpen,
    onCreateMessageChange,
    updateDialogs,
    updateMessageCount,
    preparedMessage,
}) => {
    const { t, locale } = useI18n('Support');
    const router = useRouter();

    const [question, setQuestion] = useState<string>('');
    const [selectedTheme, setSelectedTheme] = useState<ISupportThemesAttributes | null>(null);
    const [needCreateRequest, setNeedCreateRequest] = useState<boolean>(false);
    const [messageCreating, setMessageCreating] = useState<boolean>(false);
    const [themes] = useState<ISupportThemesAttributes[]>([
        {
            id: 1,
            name: 'Order not found',
            answer: '',
        },
        {
            id: 2,
            name: 'Affiliate programs and traffic sources',
            answer: '',
        },
        {
            id: 3,
            name: 'Question about statistics',
            answer: '',
        },
        {
            id: 4,
            name: 'Tools',
            answer: '',
        },
        {
            id: 5,
            name: 'Payout problems',
            answer: '',
        },
        {
            id: 6,
            name: 'Another problem',
            answer: '',
        },
    ]);

    // Список подсказок на вопрос пользователя
    const { data: suggests, isLoading: saggestsIsLoading } = useQuestionSuggests({
        params: {
            search: question,
        },
        condition: !!question,
        dependencies: [question],
    }) as ICreateUseRequestResponse<ISupportQuestionSuggestsDataAttributesFormatted[]>;

    /**
     * @param {string} value
     * @returns {void}
     */
    const onSearchQuestionHandler = debounce(value => {
        setQuestion(value);
    }, 300);

    const onSubmitHandler = async (params: ITicketSubmitParams): Promise<void> => {
        try {
            setMessageCreating(true);

            const responce = await api.support.createSupportMessage<ICreateSupportMessage>({
                body: { ...params },
            });

            await updateMessageCount();
            if (typeof updateDialogs !== 'undefined') {
                await updateDialogs();
            }
            await onCreateMessageChange(false);

            router.push(
                `/${locale}${routes.support.dialog.replace(
                    ':id',
                    responce?.data?.attributes?.ticket_id.toString() || '',
                )}`,
            );
        } catch (e) {
            console.error(e);
            toast.error(
                <BaseToast text={t('An error occurred while sending the request')} type="error" />,
                {
                    toastId: 'create_support_message_error',
                },
            );
        } finally {
            setMessageCreating(false);
        }
    };

    return (
        <>
            <AskQuestionModal
                onSearch={input => onSearchQuestionHandler(input)}
                isOpen={askQuestionsIsOpen}
                onClose={value => {
                    onAskQuestionChange(false);
                    if (typeof value === 'string' && value.length > 0) {
                        setNeedCreateRequest(true);
                    }
                }}
                suggests={suggests}
                saggestsIsLoading={saggestsIsLoading}
                afterClose={() => {
                    if (needCreateRequest) {
                        onChooseThemeChange(true);
                    }
                }}
            />

            <ChooseThemeModal
                themes={themes}
                onSelect={theme => {
                    setSelectedTheme(theme);
                    onChooseThemeChange(false);
                    onCreateMessageChange(true);
                }}
                isOpen={chooseThemeIsOpen}
                onClose={() => onChooseThemeChange(false)}
                selectedTheme={selectedTheme}
                afterClose={() => {
                    if (selectedTheme !== null) {
                        onCreateMessageChange(true);
                    }
                }}
            />

            <CreateMessageModal
                isOpen={createMessageIsOpen}
                onClose={() => onCreateMessageChange(false)}
                selectedTheme={selectedTheme}
                question={question}
                subject={preparedMessage?.subject}
                loading={messageCreating}
                onSubmit={onSubmitHandler}
            />

            <div className="flex flex-row items-start justify-between mt-4 mb-8 xs:items-center xs:flex-row xs:space-x-4">
                <div className="hidden xs:block min-w-[167px]">
                    <Button type="primary" size="large" onClick={() => onAskQuestionChange(true)}>
                        {t('Ask question')}
                    </Button>
                </div>

                <div className="max-w-[350px] text-blueGray-500 text-p1">
                    {t('Support works 9:00 to 23:00 every day')}
                </div>
            </div>
        </>
    );
};

export default SupportHead;
